import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { useGetTargetById } from '@/generated/client/targets/targets';
import type { MinimalTarget } from '@/generated/model';
import type { TimeRange } from '@/shared/charts';

import type { TimeSeriesChartDateRange } from '../types/TimeSeriesChartDateRange.types';
import type { TimeWindowMicros } from '../types/TimeWindow.types';
import { getTimeWindowMicros } from '../utils/timeWindow';
import { getDefaultDateRange, isValidDateRange } from '../utils/timeSeriesChartDateRange';
import {
  type TimeSeriesChartUrlStateInput,
  parseTimeSeriesChartUrlState,
  updateTimeSeriesChartSearchParams,
} from '../utils/timeSeriesChartUrlState';

/**
 * `dates` gets a new identity whenever any search param changes, so the window is keyed on its
 * primitive bounds: the queries and the chart content must not react to an unrelated URL change.
 */
function useStableTimeWindow(dates: TimeSeriesChartDateRange | undefined): TimeWindowMicros | undefined {
  const { fromTimeMicros, toTimeMicros } = getTimeWindowMicros(dates) ?? {};

  return useMemo(
    () => (fromTimeMicros === undefined || toTimeMicros === undefined ? undefined : { fromTimeMicros, toTimeMicros }),
    [fromTimeMicros, toTimeMicros]
  );
}

/**
 * Which target and window the popup shows, and which panel is open. Owners:
 * - target id and date range live in the URL, so the diagram is shareable and survives a refresh;
 * - the target itself comes from the query cache, keyed by that id;
 * - search text and the open panel are plain UI state of this popup.
 *
 * The series selection has its own owner, see useSeriesSelectionPreferences.
 */
export function useTimeSeriesChartState() {
  const [searchParams, setSearchParams] = useSearchParams();
  // searchParams is stable until the URL changes, so the parsed dates keep their identity between renders.
  const { targetId, dates } = useMemo(() => parseTimeSeriesChartUrlState(searchParams), [searchParams]);

  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [isChannelSelectorOpen, setChannelSelectorOpen] = useState(targetId !== undefined);

  const targetQuery = useGetTargetById(targetId ?? 0, { query: { enabled: targetId !== undefined } });

  // The same window twice: in epoch milliseconds for the chart, in microseconds for the requests.
  const timeDomain = useMemo<TimeRange | undefined>(
    () => dates && { startTimestamp: dates.from.getTime(), endTimestamp: dates.to.getTime() },
    [dates]
  );
  const timeWindow = useStableTimeWindow(dates);

  function updateUrlState(urlState: TimeSeriesChartUrlStateInput) {
    setSearchParams((current) => updateTimeSeriesChartSearchParams(current, urlState), { replace: true });
  }

  function selectTarget(target: MinimalTarget) {
    updateUrlState({ targetId: target.id, dates: dates ?? getDefaultDateRange() });
    setChannelSelectorOpen(true);
  }

  function clearTarget() {
    updateUrlState({ targetId: undefined, dates: undefined });
    setChannelSelectorOpen(false);
  }

  function changeDates(nextDates: TimeSeriesChartDateRange) {
    if (targetId === undefined || !isValidDateRange(nextDates)) {
      return;
    }

    updateUrlState({ targetId, dates: nextDates });
  }

  function closeChannelSelector() {
    setChannelSelectorOpen(false);
  }

  function retryTarget() {
    void targetQuery.refetch();
  }

  return {
    targetId,
    selectedTarget: targetQuery.data?.data,
    isTargetError: targetQuery.isError,
    isTargetRetrying: targetQuery.isFetching,
    selectedDates: dates,
    timeDomain,
    timeWindow,
    searchText,
    isChannelSelectorOpen,

    selectTarget,
    clearTarget,
    retryTarget,
    changeDates,
    changeSearchText: setSearchText,
    closeChannelSelector,
  };
}
